import * as _ from 'underscore'

import { Enums } from '../common/enums'

import { DataProvider } from '../../helpers/data-provider'
import { Dictionary } from '../../helpers/dictionary'
import { TypeHelper } from '../../helpers/type-helper'

import { ObjectCustomString } from '../../models/util/object-custom-string'

import { IOpObject, IReferenceType, IOpChangeState, IOpData, IAlarmConfigurable, ICopyable } from '../util/interfaces'
import { OpDataList } from '../util/op-data-list'

import { OpAlarmDef } from './op-alarm-def'
import { OpConsumer } from './op-consumer'
import { OpData } from './op-data'
import { OpDataType } from './op-data-type'
import { OpDevice } from './op-device'
import { OpDistributor } from './op-distributor'
import { OpLocationStateType } from './op-location-state-type'
import { OpLocationType } from './op-location-type'
import { OpMeter } from './op-meter'
import { OpTariff } from './op-tariff'
import { Utils } from '../util/utils'


export class OpLocation
    implements IReferenceType
    , IOpObject
    , IOpChangeState
    , IAlarmConfigurable
    , ICopyable<OpLocation>
{
    // #region Properties

    public idLocation: number
    public idLocationType: number
    public idLocationPattern: number
    public idDescrPattern: number
    public idDistributor: number
    public idConsumer: number
    public idLocationStateType: number
    public inKpi: boolean
    public allowGrouping: boolean

    // #endregion

    // #region Navigational Properties

    public consumer: OpConsumer
    public distributor: OpDistributor
    public locationType: OpLocationType
    public locationStateType: OpLocationStateType

    // #endregion

    // #region Custom Properties

    public parentGroups: OpLocation[] = []
    public currentDevices: OpDevice[] = []
    public currentMeters: OpMeter[] = []

    public currentMetersDict: Dictionary<OpMeter>
    public currentDevicesDict: Dictionary<OpDevice>

    public currentAlarmDefs: OpAlarmDef[] = []

    public currentTariff: OpTariff

    public set name(value: string) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_NAME)
    }
    public get name(): string {
        return this.dataList.getValue(Enums.DataType.LOCATION_NAME)
    }

    public set cid(value: string) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_CID)
    }
    public get cid(): string {
        return this.dataList.getValue(Enums.DataType.LOCATION_CID)
    }

    public set address(value: string) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_ADDRESS)
    }
    public get address(): string {
        return this.dataList.getValue(Enums.DataType.LOCATION_ADDRESS)
    }

    public set city(value: string) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_CITY)
    }
    public get city(): string {
        return this.dataList.getValue(Enums.DataType.LOCATION_CITY)
    }

    public set postalCode(value: string) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_ZIP)
    }
    public get postalCode(): string {
        return this.dataList.getValue(Enums.DataType.LOCATION_ZIP)
    }

    public set latitude(value: number) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_LATITUDE)
    }
    public get latitude(): number {
        return this.dataList.getValue(Enums.DataType.LOCATION_LATITUDE)
    }

    public set longitude(value: number) {
        this.dataList.setValue(value, Enums.DataType.LOCATION_LONGITUDE)
    }
    public get longitude(): number {
        return this.dataList.getValue(Enums.DataType.LOCATION_LONGITUDE)
    }

    // #endregion

    // #region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpLocation) {
            this.copy(<OpLocation>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    // #endregion

    // #region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    // #endregion

    // #region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdLocation
    }
    public getReferenceKey(): number {
        return this.idLocation
    }
    public getReferenceValue(): any {
        return this
    }

    // #endregion

    // #region IOpObject

    public get idObject(): any {
        return this.idLocation
    }

    public set idObject(idObject: any) {
        this.idLocation = idObject
    }

    public dataList: OpDataList<OpData> = new OpDataList<OpData>(OpData)

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idLocation = opObj.ID_LOCATION
            this.idLocationType = opObj.ID_LOCATION_TYPE
            this.idLocationPattern = opObj.ID_LOCATION_PATTERN
            this.idDescrPattern = opObj.ID_DESCR_PATTERN
            this.idDistributor = opObj.ID_DISTRIBUTOR
            this.idConsumer = opObj.ID_CONSUMER
            this.idLocationStateType = opObj.ID_LOCATION_STATE_TYPE
            this.inKpi = opObj.IN_KPI
            this.allowGrouping = opObj.ALLOW_GROUPING

            if (opObj.DataList != null) {
                this.dataList.push(...opObj.DataList.map(data => new OpData(data)))
            }

            if (opObj.ParentGroups != null) {
                this.parentGroups = opObj.ParentGroups.map((data: any) => new OpLocation(data))
            }

            if (opObj.CurrentDevices != null) {
                this.currentDevices = opObj.CurrentDevices.map((data: any) => new OpDevice(data))
            }

            if (opObj.CurrentMeters != null) {
                this.currentMeters = opObj.CurrentMeters.map((data: any) => new OpMeter(data))
            }

            if (opObj.CurrentAlarmDefs != null) {
                this.currentAlarmDefs = opObj.CurrentAlarmDefs.map((alarmDef: any) => new OpAlarmDef(alarmDef))
            }

            if (opObj.CurrentTariff != null) {
                this.currentTariff = new OpTariff(opObj.CurrentTariff)
            }

            this.consumer = opObj._Consumer != null ? new OpConsumer(opObj._Consumer) : null
            this.distributor = opObj._Distributor != null ? new OpDistributor(opObj._Distributor) : null
            this.locationType = opObj._LocationType != null ? new OpLocationType(opObj._LocationType) : null
            this.locationStateType = opObj._LocationStateType != null ? new OpLocationStateType(opObj._LocationStateType) : null

            this.dynamicValues = opObj.dynamicValues
            this.opState = opObj.OpState
        } else {
            this.idLocation = 0
            this.opState = Enums.OpChangeState.New
        }

    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_LOCATION: this.idLocation,
            ID_DISTRIBUTOR: this.idDistributor,
            DataList: this.dataList.ToJSON(),
            OpState: this.opState,
            ParentGroups: this.parentGroups != null ? this.parentGroups.map((parentGroup: OpLocation) => parentGroup.toJSON()) : [],
            CurrentDevices: this.currentDevices != null ? this.currentDevices.map((device: OpDevice) => device.toJSON()) : [],
            CurrentMeters: this.currentMeters != null ? this.currentMeters.map((meter: OpMeter) => meter.toJSON()) : [],
            CurrentAlarmDefs: this.currentAlarmDefs != null ? this.currentAlarmDefs.map((alarmDef: OpAlarmDef) => alarmDef.toJSON()) : [],
            dynamicValues: this.dynamicValues,
            CurrentDevicesDict: Utils.serializeDictionary(this.currentDevicesDict),
            CurrentMetersDict: Utils.serializeDictionary(this.currentMetersDict),
            CurrentTariff: this.currentTariff != null ? this.currentTariff.toJSON() : null
        }
    }

    public toString(): string {
        let retStr: string = TypeHelper.emptyString

        const customFormatString: string = ObjectCustomString.getCustomStringFormat(this)

        if (!customFormatString) {
            let name: string = this.name
            let cid: string = this.cid
            let address: string = this.address

            if (this.dynamicValues != null) {
                if (TypeHelper.isNullOrEmpty(name)) {
                    const dataType: OpDataType = new OpDataType()
                    dataType.idDataType = Enums.DataType.LOCATION_NAME;
                    dataType.idDataTypeClass = Enums.DataTypeClass.Text;

                    name = Utils.extractDynamicValueByIdDataType(this, dataType)
                }

                if (TypeHelper.isNullOrEmpty(cid)) {
                    const dataType: OpDataType = new OpDataType()
                    dataType.idDataType = Enums.DataType.LOCATION_CID;
                    dataType.idDataTypeClass = Enums.DataTypeClass.Text;

                    cid = Utils.extractDynamicValueByIdDataType(this, dataType)
                }

                if (TypeHelper.isNullOrEmpty(address)) {
                    const dataType: OpDataType = new OpDataType()
                    dataType.idDataType = Enums.DataType.LOCATION_ADDRESS;
                    dataType.idDataTypeClass = Enums.DataTypeClass.Text;

                    address = Utils.extractDynamicValueByIdDataType(this, dataType)
                }
            }

            retStr = TypeHelper.isNullOrEmpty(name) ? cid : name

            if (retStr == null) {
                retStr = TypeHelper.emptyString
            }

            if (!TypeHelper.isNullOrEmpty(this.address)) {
                retStr += retStr.length > 0
                    ? ` ${address}`
                    : address
            }
        } else {
            retStr = ObjectCustomString.getObjectCustomString(customFormatString, this)
        }

        if (retStr == null) {
            retStr = TypeHelper.emptyString
        }

        return retStr
    }

    // #endregion

    // #region ICopyable<OpLocation> implementation

    public copy(sourceObject: OpLocation, includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): void {
        if (sourceObject == null) {
            return
        }

        if (this.dataList == null) {
            this.dataList = new OpDataList(OpData)
        }

        if (includeNavigationProperties) {
            if (sourceObject.consumer != null) {
                this.consumer = sourceObject.consumer.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (sourceObject.distributor != null) {
                this.distributor = sourceObject.distributor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (sourceObject.locationType != null) {
                this.locationType = sourceObject.locationType
            }

            if (sourceObject.locationStateType != null) {
                this.locationStateType = sourceObject.locationStateType
            }
        }

        if (includeCustomProperties) {
            if (sourceObject.parentGroups != null) {
                this.parentGroups = sourceObject.parentGroups.map((location: OpLocation) => location.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }

            if (sourceObject.currentDevices != null) {
                this.currentDevices = sourceObject.currentDevices.map((device: OpDevice) => device/*.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)*/)
            }

            if (sourceObject.currentDevicesDict != null) {
                this.currentDevicesDict = Dictionary.createFromDictionary(sourceObject.currentDevicesDict)
            }

            if (sourceObject.currentMeters != null) {
                this.currentMeters = sourceObject.currentMeters.map((meter: OpMeter) => meter/*.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)*/)
            }

            if (sourceObject.currentMetersDict != null) {
                this.currentMetersDict = Dictionary.createFromDictionary(sourceObject.currentMetersDict)
            }

            if (sourceObject.currentAlarmDefs != null) {
                this.currentAlarmDefs = sourceObject.currentAlarmDefs.map((alarmDef: OpAlarmDef) => alarmDef.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }

            if (sourceObject.currentTariff != null) {
                this.currentTariff = sourceObject.currentTariff.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        if (includeDataList && sourceObject.dataList != null) {
            this.dataList.push(...sourceObject
                .dataList
                .toArray()
                .map((data: OpData) => new OpData(data))
            )
        }

        if (includeDynamicValues) {
            this.dynamicValues = sourceObject.dynamicValues
        }

        this.idLocation = sourceObject.idLocation
        this.idLocationType = sourceObject.idLocationType
        this.idLocationPattern = sourceObject.idLocationPattern
        this.idDescrPattern = sourceObject.idDescrPattern
        this.idDistributor = sourceObject.idDistributor
        this.idConsumer = sourceObject.idConsumer
        this.idLocationStateType = sourceObject.idLocationStateType
        this.inKpi = sourceObject.inKpi
        this.allowGrouping = sourceObject.allowGrouping

        this.opState = sourceObject.opState
    }

    public clone(includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): OpLocation {
        const location: OpLocation = new OpLocation(null)

        location.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return location
    }

    // #endregion

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpLocation[]): JQueryPromise<OpLocation[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    public static fromIOpObject(object: IOpObject): OpLocation {
        var newObject: OpLocation = new OpLocation(null)

        newObject.idLocation = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpData.fromIOpData(data)))

        return newObject
    }
}
